import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Chip, Name, Table, Text } from '~/components/blocks';
import { ContactMethod } from '~/components/pages/Patient/AllPatientList/AllPatient/ContactMethod';
import { useMedicationFollowupSettingStatus } from '~/components/partials/PatientDetailDialog/use-medication-followup-setting-status';
import { LineVisitorOnAllPatientListFragment } from '~/graphql/generated/graphql';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { Label } from '~/utils/label';

import { CommentColumn } from './CommentColumn';
import { MessageSendAtColumn } from './MessageSendAtColumn';
import { MessageSendFromColumn } from './MessageSendFromColumn';
import { QuestionnaireSheetColumn } from './QuestionnaireSheetColumn';

type Props = {
  lineVisitor: LineVisitorOnAllPatientListFragment;
};

export const LineVisitor = React.memo(({ lineVisitor }: Props) => {
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);
  const now = new Date();
  const latestFollowupMessage =
    lineVisitor.medicationFollowupMessages.nodes.find((m) => !m.isAutoReplyMessage) || null;

  // すでに送信されたメッセージの最新を取得する
  const latestSentFollowupMessage =
    lineVisitor.medicationFollowupMessages.nodes.find(
      (m) => !m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;
  const latestSentFollowupAutoReplyMessage =
    lineVisitor.medicationFollowupMessages.nodes.find(
      (m) => m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;

  const { statusManagement } = useMedicationFollowupSettingStatus();
  const medixsPatient = lineVisitor.medixsPatient;
  const medicationFollowupStatus =
    medixsPatient?.medicationFollowupStatus || lineVisitor.medicationFollowupStatus;
  const isFollowingUp = medicationFollowupStatus === 'in_progress';
  const hasSms = medixsPatient?.guestPatients.some(
    (guestPatient) => guestPatient.__typename !== 'LineVisitor',
  );

  const handleClick = useCallback(() => {
    const guestPatientId = medixsPatient ? medixsPatient.guestPatients[0].id : lineVisitor.id;

    setPatientDetailDialogState((state) => ({
      ...state,
      isOpen: true,
      type: 'message',
      patientId: guestPatientId,
    }));
  }, [medixsPatient, setPatientDetailDialogState, lineVisitor.id]);

  return (
    <Table.Tr onClick={handleClick}>
      <Table.Td>
        <Name
          familyName={lineVisitor.familyName}
          givenName={lineVisitor.givenName}
          phoneticFamilyName={lineVisitor.phoneticFamilyName}
          phoneticGivenName={lineVisitor.phoneticGivenName}
        />
      </Table.Td>
      <Table.Td>
        <Text size="m" whiteSpace="nowrap">
          {Label.age(lineVisitor.birthDate)}({Label.sex(lineVisitor.sex)})
        </Text>
      </Table.Td>
      <Table.Td>
        <ContactMethod
          hasApp={!!medixsPatient?.patient}
          phoneNumber={hasSms ? medixsPatient?.mobilePhone : undefined}
          hasLine={lineVisitor.active}
        />
      </Table.Td>
      <Table.Td>
        <MessageSendAtColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
      <Table.Td>
        {statusManagement && isFollowingUp && (
          <Chip size="fixed" color="blue" textColor="white">
            対応中
          </Chip>
        )}
      </Table.Td>
      <Table.Td>
        <QuestionnaireSheetColumn
          latestSentFollowupMessage={latestSentFollowupMessage}
          latestSentFollowupAutoReplyMessage={latestSentFollowupAutoReplyMessage}
        />
      </Table.Td>
      <Table.Td>
        <CommentColumn patient={lineVisitor} />
      </Table.Td>
      <Table.Td>
        <MessageSendFromColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
    </Table.Tr>
  );
});

LineVisitor.displayName = 'LineVisitor';
